import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { MessageCircle, X, Loader2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export function CoachMessageBadge() {
  const { api } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/coach/messages');
      setUnreadCount(res.data.unread_count);
      if (res.data.messages?.length > 0) {
        setLatestMessage(res.data.messages[0]);
        if (res.data.messages[0].priority === 'high' && !showPopup) {
          setShowPopup(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch coach messages:', err);
    }
  };

  const handleAction = async (actionId) => {
    setActionLoading(actionId);
    try {
      if (actionId === 'adjust_next_session') {
        const res = await api.post('/coach/actions/adjust-next-session');
        if (res.data.adjusted) {
          toast.success(res.data.message);
        } else {
          toast.info(res.data.message);
        }
      } else {
        await api.post('/coach/actions/dismiss');
        toast.info('Keeping current plan');
      }
      setShowPopup(false);
      setUnreadCount(0);
      setLatestMessage(null);
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setActionLoading(null);
    }
  };

  const dismissPopup = (e) => {
    e.stopPropagation();
    setShowPopup(false);
  };

  const hasActions = latestMessage?.suggested_actions?.length > 0;

  return (
    <>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="coach-badge-count">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}

      <AnimatePresence>
        {showPopup && latestMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50"
          >
            <div className="bg-card border border-accent/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="bg-accent-deep rounded-full p-2">
                  <MessageCircle className="text-accent" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm text-accent">
                      {latestMessage.coach_name}
                    </p>
                    <button
                      onClick={dismissPopup}
                      className="text-muted-foreground hover:text-foreground p-1"
                      data-testid="popup-close-btn"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <p className="text-sm text-foreground mb-3" data-testid="popup-message-text">
                    {latestMessage.message}
                  </p>
                  {hasActions ? (
                    <div className="flex gap-2" data-testid="popup-action-buttons">
                      {latestMessage.suggested_actions.map(action => (
                        <Button
                          key={action.id}
                          size="sm"
                          disabled={actionLoading !== null}
                          onClick={(e) => { e.stopPropagation(); handleAction(action.id); }}
                          className={action.style === 'primary'
                            ? 'bg-accent text-accent-foreground hover:bg-accent/80'
                            : ''}
                          variant={action.style === 'ghost' ? 'ghost' : 'default'}
                          data-testid={`popup-action-${action.id}`}
                        >
                          {actionLoading === action.id ? (
                            <Loader2 size={14} className="animate-spin mr-1" />
                          ) : null}
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Tap to open chat
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function CoachMessageCard({ onDismiss, onHasActions }) {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/coach/messages');
      const msgs = res.data.messages || [];
      setMessages(msgs);
      onHasActions?.(msgs.length > 0 && msgs[0].suggested_actions?.length > 0);
    } catch (err) {
      console.error('Failed to fetch coach messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionId) => {
    setActionLoading(actionId);
    try {
      if (actionId === 'adjust_next_session') {
        const res = await api.post('/coach/actions/adjust-next-session');
        if (res.data.adjusted) {
          toast.success(res.data.message);
          setApplied(true);
        } else {
          toast.info(res.data.message);
        }
        setTimeout(() => { setMessages([]); onHasActions?.(false); }, 2000);
      } else if (actionId === 'keep_current_plan') {
        await api.post('/coach/actions/dismiss');
        toast.info('Keeping current plan');
        setMessages([]);
        onHasActions?.(false);
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async () => {
    try {
      await api.post('/coach/messages/read');
    } catch (err) {
      console.error('Failed to mark messages read:', err);
    }
    setMessages([]);
    onDismiss?.();
  };

  if (loading || messages.length === 0) return null;

  const latestMessage = messages[0];
  const hasActions = latestMessage.suggested_actions?.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-accent-deep to-transparent border border-accent/30 rounded-xl p-4 mb-4"
      data-testid="coach-message-card"
    >
      <div className="flex items-start gap-3">
        <div className="bg-accent-deep rounded-full p-2 shrink-0">
          <MessageCircle className="text-accent" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-accent" data-testid="coach-card-name">
              {latestMessage.coach_name}
            </p>
            {hasActions && (
              <span className="text-xs text-muted-foreground">recommends</span>
            )}
          </div>
          <p className="text-sm text-foreground mb-3" data-testid="coach-card-message">
            {latestMessage.message?.length > 120 
              ? <>{latestMessage.message.slice(0, 120).trim()}... <a href="/chat" className="text-accent text-xs font-medium">Read more</a></>
              : latestMessage.message
            }
          </p>

          {applied ? (
            <div className="inline-flex items-center gap-1.5 text-sm text-green-500 font-medium" data-testid="coach-card-applied">
              <Check size={16} /> Adjusted
            </div>
          ) : hasActions ? (
            <div className="flex gap-2" data-testid="coach-card-action-buttons">
              {latestMessage.suggested_actions.map(action => (
                <Button
                  key={action.id}
                  size="sm"
                  disabled={actionLoading !== null}
                  onClick={() => handleAction(action.id)}
                  className={action.style === 'primary'
                    ? 'bg-accent text-accent-foreground hover:bg-accent/80'
                    : ''}
                  variant={action.style === 'ghost' ? 'ghost' : 'default'}
                  data-testid={`coach-action-${action.id}`}
                >
                  {actionLoading === action.id ? (
                    <Loader2 size={14} className="animate-spin mr-1" />
                  ) : null}
                  {action.label}
                </Button>
              ))}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                data-testid="coach-card-dismiss"
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
